import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, Avatar, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, IconButton, Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { AuthContext } from '../contexts/AuthContext';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusBadge from '../components/common/StatusBadge';
import { getStudents } from '../services/studentService';

const StatCard = ({ title, value, icon, color, bgColor }) => (
  <Paper
    sx={{
      px: 2.5, py: 2, flex: 1, minWidth: 160,
      borderRadius: 3, border: '1px solid', borderColor: 'grey.100',
      display: 'flex', alignItems: 'center', gap: 2,
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: 3 },
    }}
  >
    <Avatar sx={{ bgcolor: bgColor || alpha(color, 0.1), width: 44, height: 44 }}>
      {icon}
    </Avatar>
    <Box>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: color || 'text.primary', lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const BankerPortalPage = () => {
  const navigate = useNavigate();
  const { mongoUser } = useContext(AuthContext);
  const isTeamMember = !!mongoUser?.parentBankerId;
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      const { data } = await getStudents(params);
      setStudents(data.data?.students || []);
    } catch (err) {
      console.error('Failed to fetch students:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Stats
  const totalFiles = students.length;
  const approved = students.filter((s) => ['Approved', 'Sanctioned', 'Disbursed', 'Closed'].includes(s.status)).length;
  const pending = students.filter((s) => ['Assigned to Banker', 'Under Review', 'Query Raised'].includes(s.status)).length;
  const totalAmount = students.reduce((sum, s) => sum + (s.loanAmount || 0), 0);

  const formatCurrency = (amt) => {
    if (!amt) return '₹0';
    if (amt >= 10000000) return `₹${(amt / 10000000).toFixed(1)}Cr`;
    if (amt >= 100000) return `₹${(amt / 100000).toFixed(1)}L`;
    return `₹${amt.toLocaleString('en-IN')}`;
  };

  const filteredStudents = students.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.name || '').toLowerCase().includes(q) ||
      (s.fileNumber || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q);
  });

  const paginatedStudents = filteredStudents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getDaysWithBanker = (date) => {
    if (!date) return '-';
    return Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
  };

  return (
    <Box>
      <PageHeader
        title="Banker Portal"
        subtitle={
          isTeamMember
            ? `Team Member — ${mongoUser.bankName || 'Bank'}`
            : mongoUser?.bankName
            ? mongoUser.bankName
            : undefined
        }
      />

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard
          title="Total Files"
          value={totalFiles}
          icon={<PeopleOutlineIcon sx={{ color: '#1A73E8' }} />}
          color="#1A73E8"
        />
        <StatCard
          title="Under Review"
          value={pending}
          icon={<PendingActionsIcon sx={{ color: '#F9AB00' }} />}
          color="#F9AB00"
        />
        <StatCard
          title="Approved"
          value={approved}
          icon={<CheckCircleOutlineIcon sx={{ color: '#1E8E3E' }} />}
          color="#1E8E3E"
        />
        <StatCard
          title="Total Loan Value"
          value={formatCurrency(totalAmount)}
          icon={<TrendingUpIcon sx={{ color: '#7C3AED' }} />}
          color="#7C3AED"
        />
      </Box>

      {/* Search + Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'grey.100' }}>
        {/* Search Bar */}
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}>
            Assigned Files ({filteredStudents.length})
          </Typography>
          <TextField
            size="small"
            placeholder="Search student..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 280, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem' } }}
          />
        </Box>

        {loading && students.length === 0 ? (
          <Box sx={{ py: 8 }}><LoadingSpinner /></Box>
        ) : filteredStudents.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <AccountBalanceIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary" fontSize="0.9rem">No files assigned yet</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748B', textTransform: 'uppercase', py: 1.5 }}>File #</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748B', textTransform: 'uppercase', py: 1.5 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748B', textTransform: 'uppercase', py: 1.5 }}>Course / Country</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748B', textTransform: 'uppercase', py: 1.5 }} align="right">Loan Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748B', textTransform: 'uppercase', py: 1.5 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748B', textTransform: 'uppercase', py: 1.5 }} align="center">Days</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748B', textTransform: 'uppercase', py: 1.5 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedStudents.map((s) => (
                    <TableRow
                      key={s._id}
                      hover
                      onClick={() => navigate(`/banker-portal/${s._id}`)}
                      sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 }, transition: 'background 0.15s' }}
                    >
                      <TableCell>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1A73E8' }}>
                          {s.fileNumber || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: alpha('#1A73E8', 0.1), color: '#1A73E8', fontSize: '0.8rem', fontWeight: 700 }}>
                            {(s.name || 'S')[0].toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B' }}>{s.name}</Typography>
                            {s.phone && <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>{s.phone}</Typography>}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.82rem', color: '#334155' }}>
                          {s.course && s.course !== 'Pending' ? s.course : '-'}
                        </Typography>
                        {s.country && s.country !== 'TBD' && (
                          <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>{s.country}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: s.loanAmount ? '#1E293B' : '#CBD5E1' }}>
                          {s.loanAmount ? `₹${Number(s.loanAmount).toLocaleString('en-IN')}` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={s.status} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getDaysWithBanker(s.assignedToBankerAt)}
                          size="small"
                          sx={{
                            fontWeight: 600, fontSize: '0.75rem', minWidth: 36,
                            bgcolor: getDaysWithBanker(s.assignedToBankerAt) === '-' ? 'grey.100' :
                              getDaysWithBanker(s.assignedToBankerAt) > 14 ? alpha('#D93025', 0.1) :
                              getDaysWithBanker(s.assignedToBankerAt) > 7 ? alpha('#F9AB00', 0.1) : alpha('#1E8E3E', 0.1),
                            color: getDaysWithBanker(s.assignedToBankerAt) === '-' ? 'text.disabled' :
                              getDaysWithBanker(s.assignedToBankerAt) > 14 ? '#D93025' :
                              getDaysWithBanker(s.assignedToBankerAt) > 7 ? '#F9AB00' : '#1E8E3E',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/banker-portal/${s._id}`); }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredStudents.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default BankerPortalPage;
